// systemDetails — build the system-details field list (Controller / System / Serial / Type / Version /
// Online / Storage / Days recording / Viewer) + the "copy all" text from the server slice. Shared by
// the desktop bottom-bar ⓘ popover (SystemInfoPopover) and the Setup > System Info page (#86bb5dx62)
// so the two can never drift. Pure functions — pass the relevant server-slice props in.

import { APP_VERSION_LABEL } from '../version';
import { statusVersionLabel } from './releaseVersion';

// eView shows the login controller as a separate level; dview has none (the breadcrumb is the NVR name).
function controllerName({ fEview, authServers, controllerSerial, sName }) {
  if (!fEview) return null;
  const ctrl = (authServers || []).find((s) => s.bSerial === controllerSerial);
  return (ctrl && ctrl.sName) || sName || '';
}

/**
 * @param {object} s { sName, fEview, bSerial, activeServerSerial, sVersion, authServers,
 *                     usersOnline, machineStatus, controllerSerial, userName,
 *                     isCloud, cloudApiVersion }
 * @returns {Array<{k: string, v: string, muted?: boolean, copyOnly?: boolean}>}
 *   copyOnly rows appear in buildSystemCopyText but are not rendered.
 */
export function buildSystemFields(s) {
  const { sName, fEview, bSerial, activeServerSerial, sVersion, sReleaseVersion, fReleaseCandidate, usersOnline, machineStatus, userName, isCloud, cloudApiVersion } = s;

  // CLOUD (#86bb5dx62): the cloud session has no per-device serial / DVS version / storage /
  // days-recording / eView controller, so GATE those rows (they'd render empty '—'). Show what
  // cloud DOES have: the account label (System), Type = "Cloud", the cloudapi version (David:
  // "Cloud API vX"), online count IF wired, the current user, and the viewer app version.
  if (isCloud) {
    const list = [];
    list.push({ k: 'System', v: sName || '' });
    list.push({ k: 'Type', v: 'Cloud' });
    if (cloudApiVersion) list.push({ k: 'Version', v: `Cloud API v${cloudApiVersion}` });
    if (Array.isArray(usersOnline)) list.push({ k: 'Online', v: String(usersOnline.length) });
    if (userName) list.push({ k: 'User', v: userName });
    list.push({ k: 'Viewer', v: APP_VERSION_LABEL, muted: true });
    return list;
  }
  const serial = fEview ? (activeServerSerial || bSerial) : bSerial;
  const controller = controllerName(s);
  // Online = the COUNT only for everyone (privacy — David 2026-08-02). The current user still sees
  // their OWN name below (the "User" row) so support knows who sent the report.
  // TODO(#86bb5dx62): show the online usernames (· dividia, robertn, …) ONLY to the `dividia`
  // account; other users keep the count alone. Not gated yet — count-only for all.
  const onlineText = Array.isArray(usersOnline) ? String(usersOnline.length) : '—';
  const storageText = (machineStatus && machineStatus.sUsed && machineStatus.sTotal)
    ? `${machineStatus.sUsed} / ${machineStatus.sTotal}${machineStatus.sPercent ? ` (${machineStatus.sPercent})` : ''}`
    : '—';
  // Just the number for brevity — the "Days recording" label carries the unit; 0 shows as 0 (David).
  const daysText = (machineStatus && typeof machineStatus.days === 'number')
    ? String(machineStatus.days)
    : '—';
  const list = [];
  if (fEview && controller) list.push({ k: 'Controller', v: controller });
  list.push({ k: 'System', v: sName || '' });
  list.push({ k: 'Serial', v: String(serial || '') });
  list.push({ k: 'Type', v: fEview ? 'eView' : 'dView' });
  const nvrVersion = statusVersionLabel({ sVersion, sReleaseVersion, fReleaseCandidate });
  if (nvrVersion) list.push({ k: 'Version', v: nvrVersion });
  // The internal compatibility version goes only into "Copy all", for support,
  // labelled: users see the release (ADR-183, D2).
  if (sReleaseVersion && sVersion) list.push({ k: 'Spec version', v: sVersion, muted: true, copyOnly: true });
  list.push({ k: 'Online', v: onlineText });
  list.push({ k: 'Storage', v: storageText });
  list.push({ k: 'Days recording', v: daysText });
  if (userName) list.push({ k: 'User', v: userName });   // the current viewer — shown so support knows the sender
  list.push({ k: 'Viewer', v: APP_VERSION_LABEL, muted: true });
  return list;
}

/** Copy-all text — every visible field row, one per line (the current user is now a field). */
export function buildSystemCopyText(fields) {
  return fields.map((f) => `${f.k}: ${f.v}`).join('\n');
}
